import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Icon from "../AppIcon";
import { useToast, ToastRenderer } from "../../hooks/useToast";
import { cn } from "../../utils/cn";
import { BROCHURE_MODULES, type ModuleSlug } from "../../data/brochureModules";
import { Roles } from "../../models/general";
import { track, AnalyticsEvents } from "../../utils/analytics";

/**
 * Phase 5 — LockedShell wraps a routed page that's been gated to Free-tier
 * users. The point is to keep the silhouette of the feature visible (so the
 * user knows what they're missing) while making the surface completely inert.
 *
 * Defensive layers — any one of them on its own would be defeated by some
 * keyboard or assistive-tech path; the combination keeps the page genuinely
 * non-interactive:
 *
 *   1. A transparent absolutely-positioned overlay sits ON TOP of the page
 *      content. It captures clicks/taps and routes the user to the brochure
 *      page. cursor-not-allowed signals the state at the hover layer.
 *
 *   2. The children container has `pointer-events: none` so even if the
 *      overlay is somehow defeated (e.g. a portal renders above it), the
 *      content beneath can't be clicked or hovered.
 *
 *   3. The children container is `aria-hidden="true"` — screen readers
 *      skip the preview entirely and read the status strip / corner chip
 *      instead, which describe the state in plain language.
 *
 *   4. A useEffect sweeps every focusable descendant on mount and on any
 *      DOM mutation, snapshots its `tabindex` attribute, and sets it to
 *      `-1`. Tab and Shift+Tab can't focus anything inside. On unmount the
 *      original tab-order is restored so the page works again the moment
 *      the user upgrades and re-renders without the wrapper.
 *
 *   5. A scoped <style> block softens the look of any disabled-style input
 *      that might otherwise inherit the page's full vibrancy and undercut
 *      the "preview" message.
 *
 * The sticky strip and corner chip are the only interactive elements — both
 * route to /brochure/<slug> (Phase 6 owns those pages; a placeholder is fine
 * for now). The CTA copy adapts to whether the user can start a trial or
 * already has one running.
 */

export interface LockedShellProps {
  moduleSlug: ModuleSlug;
  // children is intentionally ignored — see the placeholder rationale below.
  children?: React.ReactNode;
}

const LockedShell: React.FC<LockedShellProps> = ({ moduleSlug }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const role = useSelector((s: { user?: { role?: string } }) => s.user?.role);
  const { toast, showToast, dismissToast } = useToast();
  const isRtl = i18n.dir() === "rtl";

  /*
    Billing is HR/Admin's job — /settings/billing is a RoleProtectedRoute. Same gate, same nudge
    copy as SubscriptionBanner, so a Manager is never invited to a page they'd be bounced from.
  */
  const canManageBilling =
    role === Roles.Hr || role === "Hr" || role === Roles.Admin || role === "Admin";

  // Look up the module's accent palette + name. Falls back gracefully if a
  // slug ever slips through that isn't in the brochure catalog (shouldn't
  // happen because getLockedModule only returns ModuleSlug values).
  const moduleEntry = useMemo(
    () => BROCHURE_MODULES.find((m) => m.slug === moduleSlug),
    [moduleSlug],
  );
  const moduleName = moduleEntry ? t(moduleEntry.nameKey) : moduleSlug;
  const accent = moduleEntry?.accent ?? {
    surface: "bg-slate-100",
    iconText: "text-slate-700",
    ring: "hover:ring-slate-300/60",
  };

  const goToBrochure = useCallback(
    (e?: React.SyntheticEvent) => {
      e?.preventDefault();
      e?.stopPropagation();
      navigate(`/brochure/${moduleSlug}`);
    },
    [navigate, moduleSlug],
  );

  /*
    🔴 CO-227 — THIS USED TO READ `isTrial ? t("See pricing") : t("Start free trial")`.

    With trial retired, `isTrial` is false for every tenant that can reach this shell, so the
    false branch was the ONLY branch — meaning every Free tenant was offered "Start free trial"
    twice per locked module page, in all three locales, under a band that says "See plans". Both
    buttons went to /brochure/<slug>, so it was a false promise rather than a dead link.

    The two affordances now say what they each do: the band opens the module tour, and the
    secondary button goes to the one surface that can actually change the tier — the plan picker.
  */
  const goToPlans = useCallback(
    (e?: React.SyntheticEvent) => {
      e?.preventDefault();
      e?.stopPropagation();
      track(AnalyticsEvents.SeePlansCtaClicked, { source: "locked-shell", module: moduleSlug });
      if (canManageBilling) navigate("/settings/billing");
      else showToast(t("Please ask your HR or admin to upgrade."), "info");
    },
    [canManageBilling, navigate, showToast, t, moduleSlug],
  );

  // The original design tried to render the real page underneath a grayed,
  // pointer-events-none overlay (a "silhouette" of the feature). In practice
  // those pages still mount their data hooks, every fetch 403s, and the
  // silhouette becomes an error UI ("Error loading data", "Network error") —
  // worse than no preview at all. We now render a clean static placeholder
  // instead, so no underlying page mounts, no fetches fire, no errors surface.
  // The defensive tabindex sweep + click-shield + pointer-events trickery
  // from the previous design are no longer needed.

  const pitchKey = moduleEntry?.pitchKey ?? "";

  return (
    <div className="relative">
      {/* Sticky strip at the top — same calm amber band, same CTA. */}
      <div
        role="status"
        aria-live="polite"
        className={cn(
          "flex items-center justify-between gap-3 rounded-lg border border-dashed border-amber-300 bg-amber-50 px-4 py-2.5 mb-4 text-sm rtl:flex-row-reverse",
        )}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Icon name="Lock" size={16} className="flex-shrink-0 text-amber-700" aria-hidden="true" />
          <p className="truncate text-amber-900 font-medium">
            {t("You're previewing {{module}} — upgrade to use.", { module: moduleName })}
          </p>
        </div>
        <button
          type="button"
          onClick={goToBrochure}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-md bg-amber-700 text-white px-3 py-1.5 text-xs font-semibold",
            "hover:bg-amber-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-600 focus-visible:ring-offset-1",
            "flex-shrink-0",
          )}
        >
          {t("See what's included")}
          <Icon name="ArrowRight" size={12} className={isRtl ? "rotate-180" : ""} aria-hidden="true" />
        </button>
      </div>

      {/* Static module placeholder — no underlying page mounts, no fetches fire. */}
      <section
        aria-labelledby="locked-placeholder-heading"
        className={cn(
          "relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-8 md:p-12",
          "shadow-[0_1px_2px_rgba(15,23,42,0.04)]",
          "flex flex-col items-center text-center",
        )}
      >
        {moduleEntry && (
          <span
            className={cn(
              "inline-flex h-16 w-16 items-center justify-center rounded-2xl mb-5",
              accent.surface,
            )}
          >
            <Icon
              name={moduleEntry.iconName}
              size={28}
              className={accent.iconText}
              aria-hidden="true"
            />
          </span>
        )}

        <h2
          id="locked-placeholder-heading"
          className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900"
        >
          {moduleName}
        </h2>

        {pitchKey && (
          <p className="mt-3 max-w-xl text-sm md:text-base leading-relaxed text-slate-600">
            {t(pitchKey)}
          </p>
        )}

        <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={goToBrochure}
            className={cn(
              "inline-flex items-center gap-2 rounded-lg px-5 py-2.5",
              "bg-amber-700 text-sm font-semibold text-white shadow-sm",
              "hover:bg-amber-800",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-600 focus-visible:ring-offset-2",
              "transition-all",
            )}
          >
            <Icon name="Sparkles" size={16} aria-hidden="true" />
            <span>{t("See full feature tour")}</span>
            <Icon name="ArrowRight" size={14} className={isRtl ? "rotate-180" : ""} aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={goToPlans}
            className={cn(
              "inline-flex items-center gap-2 rounded-lg px-4 py-2.5",
              "text-sm font-medium text-slate-700",
              "hover:bg-slate-100 hover:text-slate-900",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-2",
              "transition-all",
            )}
          >
            {t("See plans")}
          </button>
        </div>
      </section>
      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </div>
  );
};

export default LockedShell;
